import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Custom hook for validating username availability
 * @returns {Object} Hook utilities and state
 */
export const useUsernameValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState(null);
  const functions = getFunctions();
  const checkUsernameAvailability = httpsCallable(functions, 'checkUsernameAvailability');

  /**
   * Check if a username is available
   * @param {string} username - The username to check
   * @returns {Promise<Object>} Result object with availability status and message
   */
  const validateUsername = async (username) => {
    if (!username || username.trim() === '') {
      setValidationResult(null);
      return { isAvailable: false, message: 'Username cannot be empty' };
    }

    // Client-side format validation first - specific messages per scenario
    const trimmed = username.trim();
    if (trimmed.length < 3) {
      const result = { isAvailable: false, message: 'Username must be at least 3 characters' };
      setValidationResult(result);
      return result;
    }
    if (trimmed.length > 20) {
      const result = { isAvailable: false, message: 'Username must be 20 characters or less' };
      setValidationResult(result);
      return result;
    }
    const validCharsRegex = /^[a-z0-9_]+$/;
    if (!validCharsRegex.test(trimmed)) {
      const hasUppercase = /[A-Z]/.test(trimmed);
      const hasInvalidChars = /[^a-zA-Z0-9_]/.test(trimmed);
      const message = hasUppercase && !hasInvalidChars
        ? 'Use lowercase only'
        : 'Remove special characters';
      const result = { isAvailable: false, message };
      setValidationResult(result);
      return result;
    }

    setIsValidating(true);
    setError(null);
    
    try {
      const result = await checkUsernameAvailability({ username });
      const { isAvailable, message } = result.data;
      
      const validationResult = { isAvailable, message };
      setValidationResult(validationResult);
      setIsValidating(false);
      return validationResult;
    } catch (err) {
      let errorMessage = 'Error checking username availability. Please try again.';
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsValidating(false);
      return { isAvailable: false, message: errorMessage };
    }
  };

  /**
   * Clear validation result and error
   */
  const clearValidation = () => {
    setValidationResult(null);
    setError(null);
  };

  return {
    validateUsername,
    isValidating,
    validationResult,
    error,
    clearValidation
  };
};
